import { useMemo, useState } from 'react';
import { Check, ChevronDown, ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useWorkflowList } from '@/hooks/queries/workflow/use-workflow-list';
import type { WorkflowDetails } from '@/types/workflow-types';
import { cn } from '@/lib/utils';

const ALL_WORKFLOWS_VALUE = '__all__';

const EMPTY_WORKFLOWS: WorkflowDetails[] = [];

interface ArtifactWorkflowFilterProps {
  value: string | null;
  onChange: (workflowId: string | null) => void;
}

export function ArtifactWorkflowFilter({
  value,
  onChange,
}: ArtifactWorkflowFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: workflowList, isLoading } = useWorkflowList();
  const workflows = workflowList?.workflows ?? EMPTY_WORKFLOWS;

  const sortedWorkflows = useMemo(
    () =>
      [...workflows].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [workflows],
  );

  const filteredWorkflows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedWorkflows;
    return sortedWorkflows.filter((wf) => wf.name.toLowerCase().includes(q));
  }, [sortedWorkflows, search]);

  const selectedWorkflow = useMemo(
    () => sortedWorkflows.find((wf) => wf.workflow_id === value) ?? null,
    [sortedWorkflows, value],
  );

  const triggerLabel = selectedWorkflow?.name ?? 'All workflows';

  const handleSelect = (workflowId: string | null) => {
    onChange(workflowId);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          className='h-9 max-w-[220px] gap-1 px-2 text-sm font-medium'
          aria-label='Filter by source workflow'
        >
          <ListFilter className='size-4 shrink-0' />
          <span className='truncate'>
            {isLoading ? 'Workflows…' : triggerLabel}
          </span>
          <ChevronDown className='size-4 shrink-0 text-muted-foreground' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[280px] p-0' align='end'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Search workflows...'
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No workflows found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={ALL_WORKFLOWS_VALUE}
                onSelect={() => handleSelect(null)}
              >
                <Check
                  className={cn(
                    'mr-2 size-4',
                    value === null ? 'opacity-100' : 'opacity-0',
                  )}
                />
                All workflows
              </CommandItem>
              {filteredWorkflows.map((workflow: WorkflowDetails) => (
                <CommandItem
                  key={workflow.workflow_id}
                  value={workflow.workflow_id}
                  onSelect={() => handleSelect(workflow.workflow_id)}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value === workflow.workflow_id
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  <span className='truncate'>{workflow.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
